import type { CeoDebtInstrument } from '@shared/api';
import { formatDateRu, formatMillions, formatMoney } from '../ceoFormat';
import { KpiBox, KpiGrid, KpiHint, KpiLabel, KpiValue } from './styles';

interface DebtDetailKpiGridProps {
  inst: CeoDebtInstrument;
  recalculatedEffectiveRatePct: number;
  cbrKeyRatePct: number;
  totalPaidToDate: number;
  remainingToPay: number;
}

export function DebtDetailKpiGrid({
  inst,
  recalculatedEffectiveRatePct,
  cbrKeyRatePct,
  totalPaidToDate,
  remainingToPay,
}: DebtDetailKpiGridProps) {
  const isGuarantee = inst.instrument_type === 'bank_guarantee';

  return (
    <KpiGrid>
      <KpiBox>
        <KpiLabel>
          {isGuarantee
            ? 'Лимит гарантии (забалансовое обязательство)'
            : 'Общий лимит договора'}
        </KpiLabel>
        <KpiValue>{formatMillions(inst.total_limit)}</KpiValue>
        <KpiHint>
          {isGuarantee
            ? `Действующая сумма БГ: ${formatMillions(inst.drawn_amount)}`
            : `Выбрано: ${formatMillions(inst.drawn_amount)}`}
        </KpiHint>
      </KpiBox>
      <KpiBox>
        <KpiLabel>
          {isGuarantee
            ? 'Балансовый долг (выплата по БГ)'
            : 'Текущий остаток долга'}
        </KpiLabel>
        <KpiValue>{formatMillions(inst.principal_balance)}</KpiValue>
        <KpiHint>
          {isGuarantee && inst.principal_balance === 0
            ? 'Забалансовое обязательство (не раскрыта)'
            : `Доступный лимит: ${formatMillions(inst.available_limit)}`}
        </KpiHint>
      </KpiBox>
      <KpiBox>
        <KpiLabel>
          {isGuarantee
            ? 'Ставка комиссии БГ'
            : 'Эффективная ставка'}
        </KpiLabel>
        <KpiValue>{recalculatedEffectiveRatePct.toFixed(2)}% годовых</KpiValue>
        <KpiHint>
          {isGuarantee
            ? 'Комиссия банка за выдачу и обслуживание БГ'
            : inst.rate_type === 'floating_cbr'
              ? `Плавающая: КС ЦБ (${cbrKeyRatePct.toFixed(1)}%) + ${inst.margin_pct.toFixed(2)}%`
              : inst.rate_type === 'subsidized'
                ? 'Льготная субсидируемая ставка'
                : 'Фиксированная ставка по договору'}
        </KpiHint>
      </KpiBox>
      <KpiBox>
        <KpiLabel>Ближайший платёж</KpiLabel>
        <KpiValue>
          {inst.next_payment_amount > 0
            ? formatMoney(inst.next_payment_amount)
            : 'Погашено'}
        </KpiValue>
        <KpiHint>
          Дата списания: {formatDateRu(inst.next_payment_date)} (день месяца: {inst.payment_day})
        </KpiHint>
      </KpiBox>
      <KpiBox>
        <KpiLabel>Исполнение графика</KpiLabel>
        <KpiValue>{formatMillions(totalPaidToDate)}</KpiValue>
        <KpiHint>
          {isGuarantee
            ? `Остаток комиссий по БГ: ${formatMillions(remainingToPay)}`
            : `Осталось выплатить с %: ${formatMillions(remainingToPay)}`}
        </KpiHint>
      </KpiBox>
    </KpiGrid>
  );
}
